/**
 * One builder per unit model (ADR-0075), keyed by the unit's content key. A builder adds
 * meshes to the group in model space (+x forward, +y up) with the ground at y = 0, painting
 * the owner's colour where it shows best. Units without a builder are drawn as discs.
 */

import * as T from 'three';
import { COL, horse, kit, mat, person, shadeOf, type V3 } from './parts.js';
import { ANCIENT } from './units-ancient.js';
import { MEDIEVAL } from './units-medieval.js';
import { MODERN } from './units-modern.js';

export type ModelBuilder = (g: T.Group, civ: number) => void;

const FIRST: Record<string, ModelBuilder> = {
  spearman(g, civ) {
    const k = person(g, civ, {
      helmet: 'bronze',
      greaves: true,
      cuirass: true,
      lHand: [1.6, 9.0, 2.2],
      rHand: [1.3, 9.8, -1.9],
      legs: COL.SKIN,
      sleeve: COL.SKIN,
    });
    k.bar([1.3, 0.2, -1.9], [1.3, 19.0, -1.9], 0.14, 0.16, COL.WOOD, 6);
    k.cone([1.3, 19.0, -1.9], [1.3, 21.2, -1.9], 0.34, COL.BRONZE, 6);
    k.bar([1.3, 0, -1.9], [1.3, 0.6, -1.9], 0.18, 0.18, COL.BRONZE, 6);
    // Round shield: bronze rim and boss, the owner's colour on its face.
    const shield = new T.Group();
    const face = new T.Mesh(new T.CylinderGeometry(3.0, 3.0, 0.4, 20), [mat(COL.BRONZE), mat(civ), mat(COL.BRONZE)]);
    face.rotation.x = Math.PI / 2;
    shield.add(face);
    shield.add(new T.Mesh(new T.TorusGeometry(3.0, 0.22, 6, 24), mat(COL.BRONZE)));
    const boss = new T.Mesh(new T.IcosahedronGeometry(0.55, 1), mat(COL.BRONZE));
    boss.position.z = 0.3;
    shield.add(boss);
    const ring = new T.Mesh(new T.TorusGeometry(1.6, 0.14, 4, 20), mat(COL.WHITE));
    ring.position.z = 0.22;
    shield.add(ring);
    shield.position.set(2.0, 8.8, 2.6);
    shield.rotation.y = -0.35;
    g.add(shield);
  },

  archer(g, civ) {
    const k = person(g, civ, {
      helmet: 'cap',
      lHand: [3.2, 11.2, 1.0],
      lElbow: [1.6, 11.4, 1.8],
      rHand: [-0.3, 11.4, -0.4],
      rElbow: [-1.6, 11.0, -1.6],
    });
    // A tall recurve bow held out front, string drawn to the cheek, arrow nocked.
    const points: V3[] = [];
    for (let i = 0; i <= 12; i++) {
      const t = i / 12 - 0.5;
      const tip = Math.abs(t) > 0.42 ? (Math.abs(t) - 0.42) * 6 : 0;
      points.push([3.4 - 2.6 * t * t * 4 * 0.35 + tip, 11.2 + t * 12.0, 1.0]);
    }
    for (let i = 0; i < points.length - 1; i++) {
      k.bar(points[i]!, points[i + 1]!, 0.16, 0.16, i === 5 || i === 6 ? COL.LEATHER : COL.WOODD, 5);
    }
    const nock: V3 = [-0.3, 11.4, -0.2];
    k.bar(points[0]!, nock, 0.035, 0.035, COL.WHITE, 3);
    k.bar(points[12]!, nock, 0.035, 0.035, COL.WHITE, 3);
    k.bar(nock, [5.0, 11.3, 0.9], 0.07, 0.07, COL.WOOD, 4);
    k.cone([5.0, 11.3, 0.9], [5.9, 11.3, 0.95], 0.18, COL.IRON, 4);
    k.bar([-1.3, 8.6, -0.6], [-1.6, 13.2, 0.3], 0.55, 0.6, COL.LEATHER, 8);
    for (const dz of [-0.2, 0, 0.25]) for (const dx of [-0.15, 0.15]) k.box(0.08, 0.9, 0.3, -1.7 + dx, 13.8, 0.3 + dz, civ);
    k.bar([-1.2, 12.6, 1.2], [0.9, 8.0, -1.2], 0.08, 0.08, COL.LEATHER, 4);
  },

  knight(g, civ) {
    horse(g, civ, { caparison: true });
    const rider = new T.Group();
    rider.position.set(-0.4, 4.3, 0);
    g.add(rider);
    // Shield arm forward on the near side; lance arm couched on the far side.
    const k = person(rider, civ, {
      helmet: 'great',
      seated: true,
      legs: COL.STEEL,
      boots: COL.STEEL,
      sleeve: COL.STEEL,
      pauldron: COL.STEEL,
      lHand: [2.2, 8.4, 1.6],
      lElbow: [1.2, 9.4, 2.4],
      rHand: [1.4, 8.6, -1.9],
      rElbow: [-0.6, 9.2, -2.3],
    });
    k.box(0.08, 3.2, 0.5, 1.27, 9.3, 0, COL.WHITE);
    k.box(0.08, 0.5, 1.9, 1.27, 10.0, 0, COL.WHITE);
    // Kite shield on the left arm, with a cross.
    const s = new T.Shape();
    s.moveTo(-1.5, 1.6);
    s.quadraticCurveTo(0, 2.1, 1.5, 1.6);
    s.quadraticCurveTo(1.5, -1.4, 0, -3.4);
    s.quadraticCurveTo(-1.5, -1.4, -1.5, 1.6);
    const shield = new T.Mesh(new T.ExtrudeGeometry(s, { depth: 0.3, bevelEnabled: false }), [mat(civ), mat(COL.STEEL)]);
    shield.position.set(1.4, 9.4, 2.7);
    shield.rotation.y = -0.3;
    rider.add(shield);
    k.box(0.45, 4.2, 0.06, 1.3, 9.2, 3.02, COL.WHITE, 0, -0.3);
    k.box(2.4, 0.45, 0.06, 1.3, 10.1, 3.02, COL.WHITE, 0, -0.3);
    // The lance runs under the right arm, past the hand: striped, with a vamplate and pennant.
    const lanceA: V3 = [-4, 8.3, -1.95];
    const lanceB: V3 = [17, 10.4, -2.1];
    k.bar(lanceA, lanceB, 0.16, 0.22, civ, 6);
    k.cone(lanceB, [18.8, 10.58, -2.12], 0.26, COL.STEEL, 6);
    k.cone([2.9, 8.73, -1.96], [1.8, 8.62, -1.95], 0.75, COL.STEEL, 10);
    k.shape([[0, 0], [2.8, 0.2], [1.8, -0.6], [2.6, -1.4], [0, -1.2]], 0, civ, 13.6, 10.1, -2.1);
  },

  catapult(g, civ) {
    const k = kit(g);
    for (const z of [1.9, -1.9]) {
      k.box(11, 0.9, 0.9, 0, 2.2, z, COL.WOOD);
      k.bar([-2.2, 2.6, z], [0.2, 8.4, z * 0.75], 0.32, 0.32, COL.WOOD, 4);
      k.bar([2.6, 2.6, z], [0.2, 8.4, z * 0.75], 0.32, 0.32, COL.WOOD, 4);
      k.box(6, 0.5, 0.05, 0, 2.2, z * 1.24, civ);
    }
    for (const x of [-4.8, -1.0, 4.8]) k.box(0.8, 0.8, 4.6, x, 2.2, 0, COL.WOODD);
    k.bar([0.2, 8.4, -1.6], [0.2, 8.4, 1.6], 0.4, 0.4, COL.WOODD, 6);
    k.box(0.7, 0.7, 3.0, 0.6, 8.6, 0, COL.LEATHER);
    for (const x of [-3.6, 3.6]) for (const z of [2.7, -2.7]) k.wheel(x, 1.8, z, 1.6, COL.WOODD, 6);
    k.bar([3.4, 2.8, 0], [-5.8, 7.0, 0], 0.32, 0.26, COL.WOOD, 5);
    k.bar([3.4, 2.9, -1.5], [3.4, 2.9, 1.5], 0.75, 0.75, COL.ROPE, 8);
    for (let z = -1.2; z <= 1.2; z += 0.6) k.bar([3.4, 2.15, z], [3.4, 3.65, z + 0.2], 0.08, 0.08, COL.LEATHER, 4);
    k.ball(0.95, -6.2, 7.3, 0, COL.LEATHER, 1, 1, 0.55, 1);
    k.ball(0.6, -6.2, 7.8, 0, 0xa49f94, 0);
    k.bar([-5.0, 3.0, -1.4], [-5.0, 3.0, 1.4], 0.45, 0.45, COL.ROPE, 8);
    k.bar([-5.0, 3.4, 0], [-5.4, 6.6, 0], 0.06, 0.06, COL.ROPE, 4);
    for (const z of [1.6, -1.6]) k.box(0.3, 1.6, 0.3, -5.0, 3.2, z, COL.WOODD);
    k.bar([-5.4, 2.6, 1.9], [-5.4, 10.5, 1.9], 0.08, 0.08, COL.WOODD, 4);
    k.shape([[0, 0], [2.4, -0.4], [0, -1.4]], 0, civ, -5.4, 10.4, 1.9);
  },

  musketeer(g, civ) {
    const k = person(g, civ, {
      helmet: 'tricorn',
      coat: true,
      crossbelts: true,
      legs: COL.WHITE,
      boots: COL.BLACK,
      belt: COL.WHITE,
      lHand: [1.4, 9.8, 1.0],
      rHand: [1.1, 12.6, -1.3],
      rElbow: [0.6, 10.3, -2.3],
    });
    for (let y = 8.4; y <= 11.2; y += 0.7) k.box(0.12, 0.2, 0.2, 1.3, y, 0.45, COL.BRONZE);
    k.bar([1.4, 9.6, 1.1], [0.2, 12.4, -1.3], 0.18, 0.18, COL.WOOD, 5);
    k.box(1.6, 0.55, 0.45, 1.6, 9.0, 1.15, COL.WOOD, 0, 0, -1.1);
    k.bar([0.2, 12.4, -1.3], [-1.2, 16.6, -1.9], 0.12, 0.12, COL.IRON, 6);
    k.bar([0.6, 11.6, -1.2], [-0.6, 15.0, -1.75], 0.05, 0.05, COL.WOODD, 4);
    k.box(1.0, 0.8, 0.5, -0.9, 7.6, 1.2, COL.BLACK);
    k.bar([-0.6, 7.3, -1.3], [-2.6, 4.2, -1.5], 0.1, 0.12, COL.LEATHER, 5);
  },

  cannon(g, civ) {
    const k = kit(g);
    const trail: Array<[number, number]> = [[-7.5, 0], [-6.8, 1.1], [1.8, 4.0], [3.4, 4.0], [3.4, 2.8], [1.6, 1.6], [-6.6, -0.2]];
    for (const z of [1.05, -1.05]) k.shape(trail, 0.55, shadeOf(civ, 0.95), 0, 0.7, z - 0.27);
    k.box(1.0, 0.6, 2.6, -6.9, 0.9, 0, COL.IRON);
    k.box(1.2, 0.5, 2.6, 1.6, 3.2, 0, civ);
    k.bar([-1.8, 5.0, 0], [8.6, 6.2, 0], 0.95, 0.62, COL.IRON, 12);
    k.bar([-2.2, 4.95, 0], [-1.5, 5.05, 0], 1.05, 1.05, COL.IRON, 12);
    for (const t of [0.12, 0.45, 0.8]) {
      const x = -1.8 + t * 10.4;
      const y = 5.0 + t * 1.2;
      const r = 0.95 - t * 0.33 + 0.1;
      k.bar([x - 0.18, y, 0], [x + 0.18, y + 0.02, 0], r, r, COL.IRON, 12);
    }
    k.bar([8.4, 6.18, 0], [8.9, 6.24, 0], 0.78, 0.78, COL.IRON, 12);
    k.ball(0.45, -2.6, 4.9, 0, COL.IRON, 1);
    k.bar([1.6, 5.4, 1.4], [1.6, 5.4, -1.4], 0.28, 0.28, COL.IRON, 8);
    for (const z of [1.95, -1.95]) k.wheel(1.6, 3.6, z, 3.5, COL.WOODD, 12, 0.38);
    for (const [x, z, y] of [[-4, 2.9, 0.45], [-3.1, 2.9, 0.45], [-3.55, 3.6, 0.45], [-3.55, 3.2, 1.2]] as V3[]) k.ball(0.45, x, y, z, COL.BLACK, 1);
    k.bar([-7.8, 0.25, -3.0], [1.0, 0.25, -3.2], 0.12, 0.12, COL.WOOD, 5);
    k.bar([1.0, 0.25, -3.2], [1.5, 0.25, -3.2], 0.35, 0.35, COL.ROPE, 8);
  },

  tank(g, civ) {
    const k = kit(g);
    const hull = shadeOf(civ, 0.92);
    for (const z of [2.6, -2.6]) {
      k.shape([[-6.4, 0.4], [6.0, 0.4], [7.4, 2.0], [6.6, 3.1], [-6.8, 3.1], [-7.6, 1.6]], 1.6, COL.BLACK, 0, 0, z - 0.8);
      for (let x = -5; x <= 4.6; x += 1.9) k.bar([x, 1.4, z + 0.82], [x, 1.4, z + 0.95], 0.9, 0.9, COL.IRON, 10);
      k.bar([6.2, 2.0, z + 0.82], [6.2, 2.0, z + 0.95], 0.95, 0.95, COL.IRON, 8);
      k.bar([-6.4, 2.0, z + 0.82], [-6.4, 2.0, z + 0.95], 0.85, 0.85, COL.IRON, 8);
      for (let x = -6; x <= 6; x += 0.9) k.box(0.25, 0.12, 1.62, x, 3.15, z, COL.IRON);
      k.box(13.8, 0.25, 1.9, 0, 3.3, z, hull);
    }
    k.shape([[-7.2, 2.8], [6.0, 2.8], [8.0, 4.2], [7.0, 5.6], [-6.6, 5.6], [-7.4, 4.4]], 4.0, hull, 0, 0, -2.0);
    k.bar([-0.4, 5.6, 0], [-0.4, 7.9, 0], 2.6, 3.0, civ, 8).rotation.y = Math.PI / 8;
    k.box(1.4, 1.6, 2.0, 2.5, 6.7, 0, hull);
    k.bar([3.0, 6.8, 0], [11.0, 7.0, 0], 0.32, 0.36, COL.IRON, 8);
    k.bar([10.6, 7.0, 0], [11.6, 7.02, 0], 0.5, 0.5, COL.IRON, 8);
    k.bar([-1.0, 7.9, 0.9], [-1.0, 8.4, 0.9], 0.85, 0.85, hull, 10);
    k.bar([-1.0, 8.4, 0.9], [-1.0, 8.6, 0.9], 0.7, 0.7, COL.IRON, 10);
    k.bar([0.4, 8.2, -1.2], [2.6, 8.4, -1.2], 0.1, 0.1, COL.BLACK, 5);
    k.bar([-2.4, 7.9, -1.6], [-3.0, 13.5, -1.6], 0.04, 0.04, COL.BLACK, 3);
    k.box(3.0, 0.2, 0.2, -3.2, 5.7, 1.6, COL.WOOD);
    k.box(1.8, 0.4, 1.2, -6.0, 5.8, 0, COL.IRON);
    for (const z of [2.76, -2.76]) k.disc(0.9, 5, COL.WHITE, -0.4, 6.75, z, z > 0 ? 0 : Math.PI);
  },

  caravel(g, civ) {
    const k = kit(g);
    k.shape([[-8.5, 4.2], [-9.2, 6.6], [-6.0, 6.4], [-6.0, 5.6], [6.0, 5.6], [9.6, 6.6], [8.8, 4.4], [6.5, 1.4], [-6.6, 1.2]], 4.4, COL.WOOD, 0, 0, -2.2);
    for (const y of [2.4, 3.6, 4.8]) for (const z of [2.22, -2.22]) k.box(15.4, 0.12, 0.06, 0, y, z, COL.WOODD);
    k.box(13, 0.4, 4.6, 0, 5.6, 0, COL.WOODD);
    k.shape([[-9.2, 6.6], [-6.0, 6.4], [-6.0, 8.0], [-9.0, 8.2]], 4.0, COL.WOOD, 0, 0, -2.0);
    k.box(3.4, 0.3, 4.2, -7.5, 8.2, 0, COL.WOODD);
    for (const z of [2.25, -2.25]) for (const x of [-3.5, -1, 1.5, 4]) k.box(0.5, 0.5, 0.05, x, 4.2, z, COL.DARK);
    for (const z of [2.22, -2.22]) k.box(13.4, 0.5, 0.08, 0, 1.0, z, civ);
    for (const [x, h] of [[3.8, 15.5], [-1.0, 19], [-6.4, 13.2]] as Array<[number, number]>) k.bar([x, 5.6, 0], [x, h, 0], 0.22, 0.18, COL.WOODD, 6);
    const sail = (x: number, top: number, len: number): void => {
      k.bar([x + len * 0.55, top - 1.0, 0], [x - len * 0.45, top - 1.0 - len * 0.55, 0], 0.1, 0.1, COL.WOODD, 4);
      k.shape(
        [[len * 0.55, -1.0], [-len * 0.45, -1.0 - len * 0.55], [-len * 0.1, -(top - 7.0)], [len * 0.5, -(top - 7.6)]],
        0,
        COL.WHITE,
        x,
        top,
        0.12,
      );
      k.box(0.5, 3.4, 0.04, x + len * 0.02, top - 4.4, 0.2, civ);
      k.box(2.6, 0.5, 0.04, x + len * 0.02, top - 4.0, 0.2, civ);
      k.box(0.5, 3.4, 0.04, x + len * 0.02, top - 4.4, 0.04, civ);
      k.box(2.6, 0.5, 0.04, x + len * 0.02, top - 4.0, 0.04, civ);
    };
    sail(3.8, 15.5, 9);
    sail(-1.0, 19, 11);
    sail(-6.4, 13.2, 7);
    k.bar([9.0, 6.4, 0], [13.0, 8.6, 0], 0.16, 0.12, COL.WOODD, 5);
    for (const z of [1.9, -1.9]) {
      k.bar([-1.0, 18.6, 0], [-6.6, 6.2, z], 0.03, 0.03, COL.DARK, 3);
      k.bar([-1.0, 18.6, 0], [4.4, 5.8, z], 0.03, 0.03, COL.DARK, 3);
      k.bar([3.8, 15, 0], [9.0, 6.4, z * 0.5], 0.03, 0.03, COL.DARK, 3);
    }
    k.bar([-1.0, 19, 0], [13.0, 8.6, 0], 0.03, 0.03, COL.DARK, 3);
    k.bar([-1.0, 19, 0], [-1.0, 20.5, 0], 0.06, 0.06, COL.WOODD, 4);
    k.shape([[0, 0], [2.6, -0.5], [0, -1.4]], 0, civ, -1.0, 20.4, 0);
    for (const z of [2.4, -2.4]) k.box(3, 0.15, 0.6, 7.2, 1.0, z, COL.WHITE, 0, 0, 0.3);
  },
};

/** Every unit model, by unit content key. */
export const MODELS: Record<string, ModelBuilder> = { ...FIRST, ...ANCIENT, ...MEDIEVAL, ...MODERN };
